import { useMutation } from '@apollo/client';
import { gql } from '__generated__';
import { UserType } from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC } from 'react';

import Columns from 'components/Columns';
import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

import { getUserRole } from 'utils/auth';
import countries from 'utils/countries-enum.json';

const UPDATE_USER_ROLE_MUTATION = gql(`
  mutation UpdateUser(
    $id: ID!
    $firstName: String!
    $middleName: String
    $lastName: String!
    $email: String!
    $phoneNumber: String!
    $dateOfBirth: String!
    $gender: GenderEnumType!
    $nationality: NationalityEnumType!
    $role: RoleEnumType!
    $password: String
  ) {
    updateUser(
      id: $id
      firstName: $firstName
      middleName: $middleName
      lastName: $lastName
      email: $email
      phoneNumber: $phoneNumber
      gender: $gender
      dateOfBirth: $dateOfBirth
      nationality: $nationality
      role: $role
      password: $password
    ) {
      id
      firstName
      middleName
      lastName
      email
      phoneNumber
      gender
      dateOfBirth
      nationality
      role
    }
  }
`);

const UpdateUser: FC<{ user: UserType }> = ({ user }) => {
  const [updateUser, { loading, error }] = useMutation(UPDATE_USER_ROLE_MUTATION);

  const userRole = getUserRole();

  return (
    <FormPanelWithReadMode
      loading={loading}
      error={error}
      onSubmit={data => {
        updateUser({
          variables: {
            id: user.id,
            firstName: data.firstName,
            middleName: data.middleName,
            lastName: data.lastName,
            email: data.email,
            phoneNumber: data.phoneNumber,
            gender: data.gender,
            dateOfBirth: dayjs(data.dateOfBirth).toISOString(),
            nationality: data.nationality,
            role: data.role,
            password: data.password,
          },
        });
      }}
      title="User Information"
    >
      <Columns number={3} bordered>
        <FormInput
          fullWidth
          type="string"
          fieldName="firstName"
          label="First Name"
          defaultValue={user.firstName || ''}
          validators={{
            required: true,
          }}
        />
        <FormInput
          fullWidth
          type="string"
          fieldName="middleName"
          label="Middle Name"
          defaultValue={user.middleName || ''}
        />
        <FormInput
          fullWidth
          type="string"
          fieldName="lastName"
          label="Last Name"
          defaultValue={user.lastName || ''}
          validators={{
            required: true,
          }}
        />
      </Columns>
      <Columns number={2} bordered>
        <FormInput
          fullWidth
          type="string"
          fieldName="email"
          label="Email"
          defaultValue={user.email || ''}
          validators={{
            required: true,
            isEmail: true,
          }}
        />
        <FormInput
          fullWidth
          type="string"
          fieldName="phoneNumber"
          label="Phone Number"
          defaultValue={user.phoneNumber || ''}
          validators={{
            required: true,
          }}
        />
      </Columns>
      <Columns number={2} bordered>
        <FormInput
          fullWidth
          type="select"
          fieldName="gender"
          label="Gender"
          defaultValue={user.gender || ''}
          options={[
            { value: 'MALE', label: 'Male' },
            { value: 'FEMALE', label: 'Female' },
            { value: 'OTHER', label: 'Other' },
          ]}
          validators={{
            required: true,
          }}
        />
        <FormInput
          fullWidth
          type="date"
          fieldName="dateOfBirth"
          label="Date of Birth"
          defaultValue={user.dateOfBirth || ''}
          validators={{
            required: true,
          }}
        />
      </Columns>
      <Columns number={2} bordered>
        <FormInput
          fullWidth
          type="select"
          fieldName="nationality"
          label="Nationality"
          defaultValue={user.nationality || 'PAPUA_NEW_GUINEA'}
          options={
            Object.keys(countries).map(key => ({
              value: key,
              label: countries[key],
            })) as { value: string; label: string }[]
          }
          validators={{
            required: true,
          }}
        />
        <FormInput
          fullWidth
          type="select"
          fieldName="role"
          label="Role"
          helperText="The role here refers to the permission roles of this dashboard not the person's organisation"
          defaultValue={user.role}
          options={[
            { value: 'EMPLOYEE', label: 'Employee' },
            { value: 'ADMIN', label: 'Admin' },
            { value: 'SUPER_ADMIN', label: 'Super Admin' },
          ]}
          validators={{
            required: true,
          }}
        />
      </Columns>
      {userRole === 'SUPER_ADMIN' ? (
        <FormInput
          fullWidth
          type="string"
          fieldName="password"
          label="Password"
          helperText="Leave this field empty if you do not want to change the password"
          defaultValue={''}
        />
      ) : (
        <></>
      )}
    </FormPanelWithReadMode>
  );
};

export default UpdateUser;
